import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Bell, BellOff, Clock, Check, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQUENCIES = [
  { value: 'default', label: 'Default (every 3 days)', desc: 'Receive reminder emails if you haven\'t responded within 3 days', icon: Bell, color: 'text-[#4ABFCF]' },
  { value: 'weekly', label: 'Weekly', desc: 'Receive at most one reminder per week', icon: Clock, color: 'text-amber-500' },
  { value: 'never', label: 'Opt out', desc: 'Don\'t send me any reminder emails', icon: BellOff, color: 'text-red-500' },
];

export default function PreferencesPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState('default');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/stakeholder-preferences/${token}`);
        setData(res.data);
        setSelected(res.data.reminder_frequency || 'default');
      } catch (err) {
        setError(err.response?.status === 404 ? 'Invalid or expired preferences link.' : 'Failed to load preferences.');
      } finally { setLoading(false); }
    };
    load();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.post(`${API_URL}/api/stakeholder-preferences/${token}`, { reminder_frequency: selected });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save preferences.');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#4ABFCF] animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Card className="max-w-md w-full mx-4">
        <CardContent className="py-12 text-center">
          <BellOff className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="preferences-page">
      <div className="max-w-lg w-full space-y-4">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-[#0F2040] text-white px-5 py-2.5 rounded-lg mb-4">
            <span className="text-sm font-bold">Seertech Solutions</span>
            <span className="text-[10px] text-[#4ABFCF] tracking-wider">LMS ESTIMATOR</span>
          </div>
          <h1 className="text-xl font-bold text-[#0F2040]">Notification Preferences</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage reminder emails for <span className="font-semibold text-[#0F2040]">{data.recipient_email}</span>
          </p>
        </div>

        {/* Frequency Options */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Reminder Frequency</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {FREQUENCIES.map(f => {
              const Icon = f.icon;
              const isSelected = selected === f.value;
              return (
                <button key={f.value} onClick={() => { setSelected(f.value); setSaved(false); }}
                  className={`w-full text-left rounded-lg border-2 p-3.5 transition-all ${
                    isSelected ? 'border-[#4ABFCF] bg-[#4ABFCF]/5' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                  data-testid={`pref-option-${f.value}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isSelected ? 'bg-[#4ABFCF]/10' : 'bg-slate-100'}`}>
                      <Icon className={`w-4 h-4 ${isSelected ? 'text-[#4ABFCF]' : f.color}`} />
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-semibold ${isSelected ? 'text-[#0F2040]' : 'text-slate-600'}`}>{f.label}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{f.desc}</p>
                    </div>
                    {isSelected && (
                      <div className="w-5 h-5 rounded-full bg-[#4ABFCF] flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Save */}
        <button onClick={handleSave} disabled={saving}
          className="w-full py-3 bg-[#0F2040] text-white rounded-lg font-semibold text-sm hover:bg-[#1A3055] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="pref-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : null}
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Preferences'}
        </button>

        {data.updated_at && (
          <p className="text-center text-[10px] text-slate-400">
            Last updated: {new Date(data.updated_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </div>
    </div>
  );
}
